import React, {useRef, useEffect, useState} from "react";
import {useAppContext} from "../context/AppContext";
import {useMutation} from "@tanstack/react-query";
import verifyUser from "../api/verifyUser";
import Spinner from "./Spinner";
import resendVerificationCode from "../api/resendVerificationCode";

type InputCodeFormProps = {
  email: string;
  code: string[];
  setCode: React.Dispatch<React.SetStateAction<string[]>>;
};

const InputCodeForm = ({code, setCode, email}: InputCodeFormProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [errorMessage, setMessageError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const {setToken} = useAppContext();

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < code.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && code[index] === "") {
      if (index > 0) {
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("Text").trim();
    const digits = pasted.replace(/\D/g, "").split("");

    if (digits.length >= 4) {
      const newCode = digits.slice(0, 4);
      setCode(newCode);
      inputRefs.current[code.length - 1]?.focus();
    } else if (digits.length > 0) {
      const newCode = [...code];
      digits.forEach((digit, i) => {
        if (index + i < code.length) {
          newCode[index + i] = digit;
        }
      });
      setCode(newCode);

      const nextIndex = index + digits.length - 1;
      if (nextIndex < code.length) {
        inputRefs.current[nextIndex]?.focus();
      }
    }
  };

  useEffect(() => {
    if (resendMessage) {
      const timer = setTimeout(() => setResendMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [resendMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setMessageError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    const storedToken = localStorage.getItem("iris-accessToken");
    if (storedToken) {
      try {
        setToken(storedToken);
      } catch (err) {
        console.error("Invalid token:", err);
      }
    }
  }, []);

  const {mutateAsync, isPending, error} = useMutation({
    mutationFn: verifyUser,
  });

  const {mutateAsync: resendAsync, isPending: isResending} = useMutation({
    mutationFn: resendVerificationCode,
    onSuccess: () =>
      setResendMessage("A new 4-digit code has been sent to your email."),
    onError: () => setResendMessage("Failed to resend code. Please try again."),
  });

  const onVerifyButtonHandler = async () => {
    const pin = code.join("");
    if (pin.length !== 4) {
      setMessageError("Enter all 4 digits of the code to continue.");
      return;
    }

    try {
      const data = await mutateAsync({email, pin});
      if (data?.data) {
        setToken(data?.data?.token);
        localStorage.setItem("iris-accessToken", data?.data?.token);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onResendHandler = async () => {
    setResendMessage("Please wait, resending email...");
    try {
      await resendAsync({email});
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="verify-card">
      <h2 className="verify-title">Verify your email</h2>
      <p className="verify-subtitle">
        A 4-digit code has been sent <br />
        to <span>{email}</span>
      </p>

      <div className="verify-inputs">
        {code.map((digit, idx) => (
          <input
            key={idx}
            type="text"
            maxLength={1}
            value={digit}
            ref={(el: any) => (inputRefs.current[idx] = el)}
            onChange={(e) => handleChange(e.target.value, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={(e) => handlePaste(e, idx)}
            onFocus={(e) => e.target.select()}
            className="verify-input"
          />
        ))}
      </div>

      <button
        className="verify-btn"
        onClick={onVerifyButtonHandler}
        disabled={isPending || isResending}>
        {isPending ? (
          <Spinner variant="small-spinner" alignSpin="center-spinner" />
        ) : (
          "Verify Email"
        )}
      </button>

      <div className="resend-section">
        <p>
          Haven't gotten the email yet?
          <br />
          <span
            onClick={onResendHandler}
            style={{cursor: isResending ? "not-allowed" : "pointer"}}>
            Click here
          </span>{" "}
          to resend your 4-digit code
        </p>
        {resendMessage && <p className="status-message">{resendMessage}</p>}
      </div>

      {error && <p className="status-message">{(error as any).message}</p>}
      {errorMessage && <p className="status-message">{errorMessage}</p>}
    </div>
  );
};

export default InputCodeForm;
